/**
 * System status page - displays health of all backend services
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { API_BASE_URL } from '../../shared/api/config';
import { LocalizedDateTimeDisplay } from '../../shared/components';
import { formatRelativeTime } from '../../shared/utils/relativeTime';
import { STANDALONE_TITLE } from '../../shared/styles/typography';

interface ServiceHealth {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unconfigured';
  latency_ms?: number;
  message?: string;
  details?: Record<string, unknown>;
}

interface HealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version: string;
  environment: string;
  services: Record<string, ServiceHealth>;
  uptime_seconds: number;
  build_timestamp?: string;
}

// Frontend build info (injected at build time via env vars)
const FRONTEND_BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || 'local';
const FRONTEND_BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || null;

const SERVICE_LABEL_KEYS: Record<string, string> = {
  backend: 'backend',
  database: 'database',
  redis: 'redis',
  claude: 'claude',
  outbound_email: 'outboundEmail',
  inbound_email: 'inboundEmail',
  simli: 'simli',
  cartesia: 'cartesia',
  e2b: 'e2b',
  ngrok: 'ngrok',
  notification_queue: 'notificationQueue',
};

// Map provider names to display names
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  sendgrid: 'SendGrid',
  ses: 'Amazon SES',
};

const STATUS_CONFIG = {
  healthy: {
    icon: CheckCircle,
    variant: 'default' as const,
    labelKey: 'healthy',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    iconColor: 'text-green-600',
  },
  degraded: {
    icon: AlertTriangle,
    variant: 'secondary' as const,
    labelKey: 'degraded',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    iconColor: 'text-yellow-600',
  },
  unhealthy: {
    icon: AlertCircle,
    variant: 'destructive' as const,
    labelKey: 'unhealthy',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    iconColor: 'text-red-600',
  },
  unconfigured: {
    icon: HelpCircle,
    variant: 'outline' as const,
    labelKey: 'unconfigured',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600',
    iconColor: 'text-gray-400',
  },
};

function formatUptime(seconds: number | undefined): string {
  if (seconds === undefined || isNaN(seconds)) return 'N/A';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

function formatTimeSince(
  t: TFunction,
  isoTimestamp: string | null | undefined
): string | null {
  if (!isoTimestamp) return null;
  const deployTime = new Date(isoTimestamp);
  if (Number.isNaN(deployTime.getTime())) return null;
  if (deployTime.getTime() > Date.now()) return null;
  return formatRelativeTime(t, deployTime);
}

function formatCommitSha(sha: string): string {
  if (!sha || sha === 'local') return sha;
  return sha.substring(0, 7);
}

function ServiceRow({ name, service }: { name: string; service: ServiceHealth }) {
  const { t } = useTranslation('common');
  const config = STATUS_CONFIG[service.status];
  const Icon = config.icon;
  const labelKey = SERVICE_LABEL_KEYS[name];

  // Build service label, appending provider name if available
  let serviceLabel = labelKey
    ? t(`systemStatus.serviceLabels.${labelKey}`)
    : name;

  // Append provider name for services that have one (e.g., "Outbound Email (SendGrid)")
  const provider = service.details?.provider as string | undefined;
  if (provider) {
    const providerDisplayName = PROVIDER_DISPLAY_NAMES[provider] || provider;
    serviceLabel = `${serviceLabel} (${providerDisplayName})`;
  }

  return (
    <div className="flex items-center py-3 px-4 border-b last:border-b-0">
      <Icon className={cn('h-5 w-5 mr-3', config.iconColor)} />
      <div className="flex-1">
        <p className="font-medium">{serviceLabel}</p>
        {service.message && (
          <p className="text-sm text-muted-foreground mt-0.5">{service.message}</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        {service.latency_ms != null && (
          <span className="text-sm text-muted-foreground">
            {t('systemStatus.latency', { ms: Math.round(service.latency_ms) })}
          </span>
        )}
        <Badge className={cn(config.bgColor, config.textColor, 'font-semibold text-xs')}>
          {t(`systemStatus.statusLabels.${config.labelKey}`)}
        </Badge>
      </div>
    </div>
  );
}

export function StatusPage() {
  const { t } = useTranslation('common');
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const data = await response.json();
      setHealth(data);
      setError(null);
    } catch {
      setError(t('systemStatus.unableToConnect'));
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchHealth is stable, only run on mount
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const overallConfig = health ? STATUS_CONFIG[health.status] : STATUS_CONFIG.unhealthy;
  const OverallIcon = overallConfig.icon;

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className={`${STANDALONE_TITLE} text-[#722F37] mb-1`}>{t('app.name')}</h1>
          <p className="text-sm text-muted-foreground">{t('systemStatus.title')}</p>
        </div>

        {/* Error state */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Overall Status Card */}
        <Card className="mb-6">
          <CardContent className="flex items-center gap-4 p-6">
            <div
              className={cn(
                'w-14 h-14 rounded-full flex items-center justify-center',
                overallConfig.bgColor
              )}
            >
              <OverallIcon className={cn('h-8 w-8', overallConfig.iconColor)} />
            </div>
            <div>
              <h2 className="text-lg font-medium">
                {health?.status === 'healthy'
                  ? t('systemStatus.allOperational')
                  : health?.status === 'degraded'
                    ? t('systemStatus.partialOutage')
                    : t('systemStatus.majorOutage')}
              </h2>
              {health && (
                <p className="text-sm text-muted-foreground">
                  {health.environment}
                  {health.environment && ' • '}
                  {t('systemStatus.uptime')}: {formatUptime(health.uptime_seconds)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Build Info Card */}
        <Card className="mb-6">
          <div className="px-4 py-3 border-b">
            <h3 className="text-sm font-medium text-muted-foreground">{t('systemStatus.buildInfo')}</h3>
          </div>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('systemStatus.backend')}</span>
              <span className="text-sm text-muted-foreground font-mono">
                {health?.version ? formatCommitSha(health.version) : t('systemStatus.notAvailable')}
                {/* Show port in local mode */}
                {(() => {
                  const port = health?.services?.backend?.details?.port;
                  return port ? (
                    <span className="ml-2 text-xs text-green-600">
                      (:{String(port)})
                    </span>
                  ) : null;
                })()}
                {health?.build_timestamp && (
                  <span className="ml-2 text-xs">
                    ({formatTimeSince(t, health.build_timestamp) || t('systemStatus.unknown')})
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('systemStatus.frontend')}</span>
              <span className="text-sm text-muted-foreground font-mono">
                {formatCommitSha(FRONTEND_BUILD_VERSION)}
                {/* Show port in local mode */}
                {FRONTEND_BUILD_VERSION === 'local' && (
                  <span className="ml-2 text-xs text-green-600">
                    (:{window.location.port || '80'})
                  </span>
                )}
                {FRONTEND_BUILD_TIMESTAMP && (
                  <span className="ml-2 text-xs">
                    ({formatTimeSince(t, FRONTEND_BUILD_TIMESTAMP) || t('systemStatus.unknown')})
                  </span>
                )}
              </span>
            </div>
            {health?.version && FRONTEND_BUILD_VERSION && health.version !== FRONTEND_BUILD_VERSION && health.version !== 'local' && FRONTEND_BUILD_VERSION !== 'local' && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-xs text-yellow-600">{t('systemStatus.versionMismatch')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Services Card */}
        {health?.services && (
          <Card>
            <div className="px-4 py-3 border-b">
              <h3 className="text-sm font-medium text-muted-foreground">{t('systemStatus.services')}</h3>
            </div>
            {Object.entries(health.services).map(([name, service]) => (
              <ServiceRow key={name} name={name} service={service} />
            ))}
          </Card>
        )}

        {/* Footer */}
        <p className="text-sm text-muted-foreground text-center mt-8">
          {t('systemStatus.lastChecked')}: {health ? <LocalizedDateTimeDisplay date={health.timestamp} dateFormat="short" /> : t('systemStatus.notAvailable')}
        </p>
      </div>
    </div>
  );
}
